/**
 * The three structure marks an AI text field may carry (correo
 * programado v2, PR1) — the portal's copy of the parser.
 *
 * The analysis JSON keeps its shape (`executive_summary`, every insight
 * `summary` / `detail` are plain strings); the structure travels INSIDE
 * the strings through exactly three marks, which the prompt asks the
 * model for and this module parses:
 *
 *   - ONE blank line separates paragraphs;
 *   - a line starting with "- " is a bullet;
 *   - a line starting with "**Label:**" is a labelled field.
 *
 * Plus inline bold, written `**text**`. Nothing else is interpreted:
 * strict on purpose, so a mark the renderer does not understand stays
 * visible as literal text instead of being quietly reshaped. Single
 * line breaks inside a paragraph are kept (`whitespace-pre-line` was
 * the behaviour before PR1).
 *
 * The email renderer has the same parser in Python
 * (`packages/delivery-channels/src/delivery_channels/text_marks.py`);
 * the two share one case table (`text-marks.test.ts` here,
 * `tests/test_text_marks.py` there).
 */

export interface MarkSpan {
  text: string;
  bold: boolean;
}

export type MarkBlock =
  | { kind: "paragraph"; lines: MarkSpan[][] }
  | { kind: "bullets"; items: MarkSpan[][] }
  | { kind: "field"; label: string; value: MarkSpan[] };

const BOLD = /\*\*(.+?)\*\*/g;
const BULLET = /^- (.*)$/;
const FIELD = /^\*\*([^*\n]+?):\*\*\s*(.*)$/;

/** Split one line into plain / bold runs. An unpaired `**` is text. */
export function parseSpans(line: string): MarkSpan[] {
  const spans: MarkSpan[] = [];
  let pos = 0;
  for (const match of line.matchAll(BOLD)) {
    const start = match.index ?? 0;
    if (start > pos) spans.push({ text: line.slice(pos, start), bold: false });
    spans.push({ text: match[1] ?? "", bold: true });
    pos = start + match[0].length;
  }
  if (pos < line.length) spans.push({ text: line.slice(pos), bold: false });
  return spans;
}

/** Parse `text` into paragraphs, bullet lists and labelled fields. */
export function parseMarks(text: string): MarkBlock[] {
  const blocks: MarkBlock[] = [];
  let paragraph: MarkSpan[][] = [];
  let bullets: MarkSpan[][] = [];
  const flushParagraph = (): void => {
    if (paragraph.length) blocks.push({ kind: "paragraph", lines: paragraph });
    paragraph = [];
  };
  const flushBullets = (): void => {
    if (bullets.length) blocks.push({ kind: "bullets", items: bullets });
    bullets = [];
  };
  for (const raw of text.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushParagraph();
      flushBullets();
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
      flushParagraph();
      bullets.push(parseSpans((bullet[1] ?? "").trim()));
      continue;
    }
    const field = FIELD.exec(line);
    if (field) {
      flushParagraph();
      flushBullets();
      blocks.push({
        kind: "field",
        label: (field[1] ?? "").trim(),
        value: parseSpans(field[2] ?? ""),
      });
      continue;
    }
    flushBullets();
    paragraph.push(parseSpans(line));
  }
  flushParagraph();
  flushBullets();
  return blocks;
}

/** Plain text: `**Label:**` → `Label:`, `**x**` → `x`. */
export function stripMarks(text: string): string {
  return text.replace(BOLD, "$1");
}

export function hasFields(blocks: MarkBlock[]): boolean {
  return blocks.some((block) => block.kind === "field");
}
